<template>
  <div class="p-4 md:p-8 w-full bg-[#F8F9FA] min-h-screen text-neutral-800">
    
    <!-- Loader -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FBB603]"></div>
    </div>

    <div v-else class="w-full space-y-8">
      <!-- Header Area -->
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Compensation Intelligence</h1>
          <p class="text-sm text-gray-400 mt-1">
            Role &rarr; Career Level &rarr; Salary Band &rarr; Job Evaluation &rarr; Job Grade &rarr; Band Position &rarr; Salary
          </p>
        </div>
      </div>

      <!-- Top Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Employees Evaluated -->
        <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
        <div>
            <div class="flex items-center justify-between mb-4">
            <h3 class="text-gray-500 text-sm font-semibold">
                Talent Evaluated
            </h3>

            <span class="bg-primary text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center whitespace-nowrap">
                {{ evaluatedPercentage }}%
            </span>
            </div>

            <div class="flex items-baseline gap-2">
            <span class="text-3xl font-bold text-gray-800">
                {{ evaluatedCount }}
            </span>

            <span class="text-gray-400 font-semibold text-lg">
                / {{ totalEmployees }}
            </span>
            </div>
        </div>

        <p class="text-xs text-gray-400 mt-4">
            {{ totalEmployees - evaluatedCount }} employees still pending evaluation
        </p>
        </div>

        <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
        <div>
            <h3 class="text-gray-500 text-sm font-semibold mb-4">
            Levels Defined
            </h3>

            <div class="text-3xl font-bold text-gray-800">
            {{ levels.length }}
            </div>
        </div>

        <p class="text-xs text-gray-400 mt-4">
            Career levels defined for the organization
        </p>
        </div>
        <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
          <div>
            <h3 class="text-gray-500 text-sm font-semibold mb-4">Talent Assigned</h3>
            <div class="text-3xl font-bold text-gray-800">{{ assignedCount }}</div>
          </div>
          <p class="text-xs text-gray-400 mt-4">Placed into evaluated roles</p>
        </div>

        <!-- Roles Defined -->
        <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
          <div>
            <div class="flex items-center justify-between mb-4">
              <h3 class="text-gray-500 text-sm font-semibold">
                Roles Defined
              </h3>

              <!-- Badge showing pending roles left to evaluate -->
              <span class="bg-primary text-white text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center whitespace-nowrap">
                +{{ rolesPendingMetricsCount }}
              </span>
            </div>

            <div class="text-3xl font-bold text-gray-800 mb-2">
              {{ roles.length }}
            </div>

            <!-- Dynamic Progress Bar -->
            <div class="w-full bg-gray-100 rounded-full h-1.5 mt-1">
              <div
                class="bg-[#FBB603] h-1.5 rounded-full transition-all duration-300"
                :style="{ width: rolesMetricsPercentage + '%' }"
              ></div>
            </div>
          </div>

          <p class="text-xs text-gray-400 mt-4">
            {{ rolesWithMetricsCount }} roles have metrics evaluated
          </p>
        </div>
      </div>

      <!-- Job Grade Distribution -->
      <div class="bg-white rounded-xl p-6 md:p-8 shadow-sm border border-gray-100">
        <div class="flex justify-between items-start md:items-center mb-8">
          <div>
            <h2 class="text-lg font-bold text-gray-900">Job grade distribution</h2>
            <p class="text-sm text-gray-500">Career level sets the band · Job grade sets the position inside it</p>
          </div>
          <span class="bg-[#FBB603] bg-opacity-20 text-yellow-800 border border-[#FBB603] border-opacity-50 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
            {{ grades.length }} grades
          </span>
        </div>

        <!-- Clean Horizontal Axis Line -->
        <!-- <div class="hidden md:flex justify-between border-b border-gray-300 pb-2 mb-6 px-4">
          <div v-for="grade in grades" :key="'axis-'+grade.id" class="text-xs font-semibold text-gray-400 text-center w-full uppercase tracking-wider">
            {{ grade.id }}
          </div>
        </div> -->

        <!-- Grade Cards Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="grade in reversedGrades" 
            :key="'card-'+grade.id" 
            class="border border-gray-200 rounded-2xl p-5 shadow-sm bg-white hover:border-gray-300 transition-colors"
          >
            <!-- Card Header -->
            <div class="flex justify-between items-center mb-1">
              <h3 class="font-bold text-gray-900 text-lg">Job Grade {{ grade.id }}</h3>
              <span class="px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs font-semibold text-gray-600 shadow-sm">
                {{ getEmployeesInGrade(grade.id).length }} candidates
              </span>
            </div>
            <p class="text-xs text-gray-400 font-medium mb-5">{{ grade.position }}% band position within career band</p>

            <!-- Roles List inside card -->
            <div class="space-y-3">
              <div 
                v-for="roleGroup in getGradeDistribution(grade.id)" 
                :key="roleGroup.roleName" 
                class="flex justify-between items-center py-2.5 border-t border-gray-100 first:border-0 first:pt-0"
              >
                <div class="flex items-center gap-2">
                  <span class="w-3 h-3 rounded-sm shadow-sm" :style="{ backgroundColor: roleColorMap[roleGroup.roleName] || '#9CA3AF' }"></span>
                  <div>
                    <p class="text-sm font-semibold text-gray-800">{{ roleGroup.roleName }}</p>
                    <p class="text-xs text-gray-500 mt-0.5">{{ roleGroup.salaryBand }}</p>
                  </div>
                </div>
                <span class="text-sm font-bold text-gray-800">{{ roleGroup.count }}</span>
              </div>
              <div v-if="getGradeDistribution(grade.id).length === 0" class="text-xs text-gray-400 italic py-2">
                No role mapped to this grade.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee Assignments Table -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div class="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white">
          <h2 class="text-lg font-bold text-gray-900">Employee assignments</h2>
          
          <div class="flex flex-col sm:flex-row items-center gap-4">
            <!-- Role Filter -->
            <select v-model="filters.role" class="border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-600 focus:outline-none focus:border-[#FBB603] min-w-[150px] shadow-sm cursor-pointer">
              <option value="">All Roles</option>
              <option v-for="role in roles" :key="role.selfId || role._id" :value="role.title">{{ role.title }}</option>
            </select>
            
            <p class="text-sm text-gray-500 font-medium whitespace-nowrap bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
              {{ filteredEmployees.length }} total · showing {{ paginatedEmployees.length }}
            </p>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm text-gray-600">
            <thead class="bg-gray-50 text-gray-500 font-semibold border-b border-gray-200">
              <tr>
                <th class="px-6 py-4">Employee</th>
                <th class="px-6 py-4">Role</th>
                <th class="px-6 py-4">Career Level</th>
                <th class="px-6 py-4">Salary Band</th>
                <th class="px-6 py-4">Job Grade</th>
                <th class="px-6 py-4">Status</th>
                <th class="px-6 py-4 text-right">Salary</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-if="paginatedEmployees.length === 0">
                <td colspan="7" class="px-6 py-10 text-center text-gray-400 border border-dashed border-gray-200 m-4 rounded-lg">No employees found matching the current filters.</td>
              </tr>
              <tr v-for="emp in paginatedEmployees" :key="emp._id" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 font-semibold text-gray-900">{{ emp.name }}</td>
                <td class="px-6 py-4 font-medium text-gray-700">
                  <div class="flex items-center gap-2">
                    <span v-if="emp.assignedRoleName !== '-'" class="w-2 h-2 rounded-full" :style="{ backgroundColor: roleColorMap[emp.assignedRoleName] || '#9CA3AF' }"></span>
                    {{ emp.assignedRoleName }}
                  </div>
                </td>
                <td class="px-6 py-4">{{ emp.careerLevel }}</td>
                <td class="px-6 py-4 text-gray-500">{{ emp.salaryBand }}</td>
                <td class="px-6 py-4">
                  <span v-if="emp.jobGrade && emp.jobGrade !== '-'" class="bg-[#FBB603] text-yellow-900 bg-opacity-20 w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-sm">
                    {{ emp.jobGrade }}
                  </span>
                  <span v-else class="text-gray-300">-</span>
                </td>
                <td class="px-6 py-4">
                  <span v-if="emp.statusFake === 'Evaluated'" class="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-sm">
                    Evaluated
                  </span>
                  <span v-else class="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700 border border-amber-200 shadow-sm">
                    Pending
                  </span>
                </td>
                <td class="px-6 py-4 text-right font-bold text-gray-800">
                  {{ emp.salary ? '€' + emp.salary.toLocaleString() : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="p-4 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          <button 
            @click="currentPage--" 
            :disabled="currentPage === 1"
            class="px-5 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-40 transition shadow-sm"
          >
            Previous
          </button>
          <span class="text-sm text-gray-500 font-semibold bg-white px-4 py-1.5 rounded-lg border border-gray-200 shadow-sm">Page {{ currentPage }} of {{ totalPages }}</span>
          <button 
            @click="currentPage++" 
            :disabled="currentPage === totalPages || totalPages === 0"
            class="px-5 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-40 transition shadow-sm"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const API_BASE_URL = process.env.VUE_APP_API_URL;
export default {
  name: "CompensationHub",
  data() {
    return {
      loading: true,
      orgId: "",
      employees: [],
      roles: [],
      grades: [],
      levels: [],
      
      roleLevelMap: {}, 
      salaryBandMap: {},

      filters: {
        role: ''
      },
      currentPage: 1,
      itemsPerPage: 5, 
    };
  },
  computed: {
    levelsCount() {
        const levels = this.levels;

        if (!levels) return 0;

        if (Array.isArray(levels)) {
            return levels.filter(level => level !== null && level !== undefined && level !== '').length;
        }

        if (typeof levels === 'object') {
            return Object.values(levels).filter(
            level => level !== null && level !== undefined && level !== ''
            ).length;
        }

        return 0;
    },
    totalEmployees() {
      return this.employees.length;
    },
    evaluatedCount() {
      return this.employees.filter(e => e.statusFake === 'Evaluated').length;
    },
     rolesWithMetricsCount() {
      if (!this.roles || !this.salaryBandMap) return 0;
      return this.roles.filter(role => {
        // Find if this role has matching salary metrics defined in the map
        const band = this.salaryBandMap[role.selfId || role._id];
        return band && band.min_salary !== null && band.max_salary !== null;
      }).length;
    },
    rolesPendingMetricsCount() {
      return this.roles.length - this.rolesWithMetricsCount;
    },
    rolesMetricsPercentage() {
      if (this.roles.length === 0) return 0;
      return Math.round((this.rolesWithMetricsCount / this.roles.length) * 100);
    },
    evaluatedPercentage() {
      if(this.totalEmployees === 0) return 0;
      return Math.round((this.evaluatedCount / this.totalEmployees) * 100);
    },
    assignedCount() {
      return this.employees.filter(e => e.assignedRoleName && e.assignedRoleName !== '-').length;
    },
    salaryBandsCount() {
      return this.levels.length || 5; 
    },
    reversedGrades() {
      return [...this.grades].reverse();
    },
    filteredEmployees() {
      let filtered = this.employees;
      if (this.filters.role) {
        filtered = filtered.filter(e => e.assignedRoleName === this.filters.role);
      }
      return filtered;
    },
    totalPages() {
      return Math.ceil(this.filteredEmployees.length / this.itemsPerPage) || 1;
    },
    paginatedEmployees() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredEmployees.slice(start, end);
    },
    roleColorMap() {
      const map = {};
      const uniqueRoles = [...new Set(
        this.employees.filter(e => e.statusFake === 'Evaluated' && e.assignedRoleName !== '-').map(e => e.assignedRoleName)
      )];
      
      const colors = ['#60A5FA', '#34D399', '#C084FC', '#FBBF24', '#F472B6', '#22D3EE', '#818CF8', '#FB7185', '#2DD4BF'];
      
      uniqueRoles.forEach((role, i) => {
        map[role] = colors[i % colors.length];
      });
      return map;
    }
  },
  watch: {
    'filters.role'() {
      this.currentPage = 1;
    }
  },
  async mounted() {
    this.orgId = this.$route.params.orgId || this.$store.state.organization?.orgData?._id || '6981c440cabb9c001a37ae84'; 
    await this.fetchDashboardData();
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem('accessToken') || 
             this.$store.state.auth?.token || 
             this.$store.state.user?.token || '';
    },

    async processInBatches(items, batchSize, asyncCallback) {
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        await Promise.all(batch.map(asyncCallback));
      }
    },

    loadPlotly() {
      return new Promise((resolve) => {
        if (window.Plotly) {
          resolve(window.Plotly);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://cdn.plot.ly/plotly-2.32.0.min.js';
        script.onload = () => resolve(window.Plotly);
        document.head.appendChild(script);
      });
    },

    async fetchDashboardData() {
      this.loading = true;
      const token = this.getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      try {
        const [rolesRes, configRes, gradesRes, employeesRes, salaryBandsRes] = await Promise.all([
          axios.get(`https://api.getboarded.tech/kvdr?orgId=${this.orgId}`, config).catch(() => ({ data: { data: [] } })),
          axios.get(`https://api-core-gateway.getboarded.com/organization/organization-config/${this.orgId}`, config).catch(() => ({ data: { data: {} } })),
          axios.get(`${API_BASE_URL}/api/v2/organization/${this.orgId}/job-grades`, config).catch(() => ({ data: { data: [] } })),
          axios.get(`https://api-core-gateway.getboarded.com/organization/employees/${this.orgId}`, config).catch(() => ({ data: { data: [] } })),
          axios.get(`${API_BASE_URL}/api/v2/organization/${this.orgId}/salary-bands`, config).catch(() => ({ data: { data: [] } }))
        ]);

        this.grades = gradesRes.data.data || [];

        this.salaryBandMap = {};
        const bands = salaryBandsRes.data.data || [];
        bands.forEach(band => { this.salaryBandMap[band.role_id] = band; });

        const orgEmployeesList = employeesRes.data.data || [];
        const validEmployeesMap = {};
        orgEmployeesList.forEach(emp => {
          const uid = emp.userId || emp._id || (emp.user && emp.user._id); 
          if (uid && emp.status !== 'DEACTIVATED' && emp.status !== 'INACTIVE') {
            validEmployeesMap[uid] = emp;
          }
        });

        let extractedRoles = [];
        const roleData = rolesRes.data.data || rolesRes.data || [];
        if (Array.isArray(roleData)) {
          roleData.forEach(item => {
            if (item.kvdrRoles && Array.isArray(item.kvdrRoles)) {
              const publishedRoles = item.kvdrRoles.filter(r => r.status === 'published');
              extractedRoles.push(...publishedRoles);
            } else if (item.title && item.status === 'published') { 
              extractedRoles.push(item);
            }
          });
        }
        this.roles = extractedRoles;

        const userIdsToFetch = new Set();
        const fetchedLevels = {};
        
        await this.processInBatches(extractedRoles, 5, async (role) => {
          if (role.kvdrRoleProfiles && Array.isArray(role.kvdrRoleProfiles)) {
            role.kvdrRoleProfiles.forEach(profile => {
              if (profile.userId) userIdsToFetch.add(profile.userId);
            });
          }

          if (!role.selfId) return;
          try {
            const res = await axios.get(`${API_BASE_URL}/api/role/${role.selfId}/level`, config);
            if (res.data && res.data.level) {
               fetchedLevels[role.selfId] = res.data.level;
            }
          } catch (e) {
             // Skip
          }
        });
        
        this.roleLevelMap = fetchedLevels;

        let userNamesMap = {};
        if (userIdsToFetch.size > 0) {
          try {
            const namesRes = await axios.post(`${API_BASE_URL}/api/users/names`, { 
              user_ids: Array.from(userIdsToFetch) 
            }, config);
            userNamesMap = namesRes.data.data || {};
          } catch (err) {
            console.error("Failed to fetch user names batch", err);
          }
        }

        let validKvdrEmployees = [];
        extractedRoles.forEach(role => {
          if (role.kvdrRoleProfiles && Array.isArray(role.kvdrRoleProfiles)) {
            role.kvdrRoleProfiles.forEach(profile => {
              const uid = profile.userId;

              const isOrgEmployee = validEmployeesMap[uid];
              if (!isOrgEmployee) return;

              const realName = userNamesMap[uid];
              if (!realName) return; 

              validKvdrEmployees.push({
                _id: profile._id,
                userId: uid,
                name: realName, 
                assignedRoleName: role.title || '-', 
                selfId: role.selfId, 
                jobGrade: '-', 
                salaryBand: '-', 
                statusFake: 'Pending',
                salary: null
              });
            });
          }
        });

        await this.processInBatches(validKvdrEmployees, 5, async (emp) => {
            if (!emp.selfId) return;
            try {
                const evalRes = await axios.get(`${API_BASE_URL}/api/v2/user-evaluations/${this.orgId}/${emp.selfId}/${emp.userId}`, config);
                if (evalRes.data && evalRes.data.data) {
                    emp.evaluationScore = evalRes.data.data.total_weighted_score;
                }
            } catch (e) {
                // Skip
            }
        });

        this.employees = validKvdrEmployees;

        const configData = configRes.data.data || {};

this.levels = configData.level?.values || [];

        this.mapData();

      } catch (error) {
        console.error("Error fetching compensation data:", error);
      } finally {
        this.loading = false;
      }
    },

    mapData() {
      const sortedGrades = [...this.grades].sort((a, b) => b.minScore - a.minScore);

      this.employees = this.employees.map((emp) => {
        const finalCareerLevel = (emp.selfId && this.roleLevelMap[emp.selfId]) 
                                ? this.roleLevelMap[emp.selfId] 
                                : 'Unassigned';

        let assignedGrade = '-';
        let status = 'Pending';
        let score = emp.evaluationScore;
        let gradePosition = 0;
        let calculatedSalary = null;

        if (score !== undefined && score !== null) {
            status = 'Evaluated';
            const matchedGrade = sortedGrades.find(g => score >= g.minScore);
            if (matchedGrade) {
                assignedGrade = matchedGrade.id;
                gradePosition = matchedGrade.position || 0; 
            }
        }

        let bandString = '-';
        const bandData = this.salaryBandMap ? this.salaryBandMap[emp.selfId] : null;
        
        if (bandData && bandData.min_salary !== null && bandData.max_salary !== null) {
            const minFormatted = parseFloat(bandData.min_salary).toLocaleString();
            const maxFormatted = parseFloat(bandData.max_salary).toLocaleString();
            bandString = `€${minFormatted} - €${maxFormatted}`;

            if (status === 'Evaluated' && gradePosition > 0) {
                const actualMin = parseFloat(bandData.min_salary);
                const actualMax = parseFloat(bandData.max_salary);
                const salaryRange = actualMax - actualMin;

                const rawSalary = actualMin + (salaryRange * (gradePosition / 100));
                calculatedSalary = Math.round(rawSalary); 
            }
        }

        return {
          ...emp,
          careerLevel: finalCareerLevel,
          jobGrade: assignedGrade,
          statusFake: status,
          salaryBand: bandString,
          salary: calculatedSalary 
        };
      });

      // RENDER PLOTLY ONCE DATA IS MAPPED
      this.$nextTick(() => {
        // Adding a tiny delay ensures the ref div is fully painted and sized by the browser
        setTimeout(() => {
          this.renderPlotlyChart();
        }, 100);
      });
    },

    // RENDER PLOTLY CHART METHOD (FIXED SIZING)
    // RENDER PLOTLY CHART METHOD
    async renderPlotlyChart() {
      if (!this.$refs.plotlyChart) return;
      
      const Plotly = await this.loadPlotly();
      
      const xGrades = this.grades.map(g => g.id); 
      const activeRoles = Object.keys(this.roleColorMap);

      const traces = activeRoles.map(role => {
        const yCounts = xGrades.map(gradeId => {
          return this.employees.filter(e => e.jobGrade === gradeId && e.assignedRoleName === role && e.statusFake === 'Evaluated').length;
        });

        return {
          x: xGrades,
          y: yCounts,
          name: role,
          type: 'bar',
          marker: {
            color: this.roleColorMap[role]
          }
        };
      });

      const layout = {
        barmode: 'stack',
        height: 380, 
        autosize: true, 
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        margin: { l: 80, r: 20, t: 40, b: 60 }, 
        
        xaxis: {
          title: {
            text: 'Job Grades',
            standoff: 15
          },
          categoryorder: 'array',
          categoryarray: xGrades,
          fixedrange: true,
          showline: true, // Draws the bottom axis line
          linecolor: '#d1d5db',
          tickfont: { color: '#6b7280', size: 11 }
        },
        yaxis: {
          title: {
            text: 'Number of Employees',
            standoff: 15
          },
          dtick: 1, 
          fixedrange: true,
          rangemode: 'nonnegative', // <--- THE FIX: Forces the bottom of the chart to be exactly 0
          gridcolor: '#f3f4f6', 
          zeroline: false, // Turn off Plotly's floating zero line so it relies on the xaxis bottom line
          tickfont: { color: '#6b7280', size: 11 }
        },
        legend: {
          orientation: "h",
          yanchor: "bottom",
          y: 1.05, 
          xanchor: "right",
          x: 1
        }
      };

      const config = { responsive: true, displayModeBar: false };

      Plotly.newPlot(this.$refs.plotlyChart, traces, layout, config);
    },

    getEmployeesInGrade(gradeId) {
      return this.employees.filter(e => e.jobGrade === gradeId);
    },

    getGradeDistribution(gradeId) {
      const empsInGrade = this.getEmployeesInGrade(gradeId);
      if(empsInGrade.length === 0) return [];

      const roleGroups = {};
      empsInGrade.forEach(emp => {
        const role = emp.assignedRoleName;
        if (!roleGroups[role]) {
          roleGroups[role] = { count: 0, salaryBand: emp.salaryBand };
        }
        roleGroups[role].count++;
      });

      return Object.keys(roleGroups).map(role => ({
        roleName: role,
        count: roleGroups[role].count,
        salaryBand: roleGroups[role].salaryBand
      })).sort((a, b) => b.count - a.count); 
    }
  }
};
</script>

<style scoped>
select {
  -webkit-appearance: none;
  -moz-appearance: none;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%234B5563" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>') no-repeat right 12px center;
  background-size: 14px;
}
</style>